import api from "../../services/api";
import { useState, useEffect } from "react";
import T from '../../assets/styles/theme';

// Modular Components
import SABadge from "../../components/SuperAdmin/SABadge";
import SAPermPill from "../../components/SuperAdmin/SAPermPill";
import SASidebar, { SA_NAV_ITEMS } from "../../components/SuperAdmin/SASidebar";
import SAHeader from "../../components/SuperAdmin/SAHeader";

// Modular Pages
import SAOverview from "./SAOverview";
import SANotifications from "./SANotifications";
import SARoles from "./SARoles";
import SAPermMatrix from "./SAPermMatrix";
import SAUserMgmt from "./SAUserMgmt";
import SACredentials from "./SACredentials";
import SAAuditLog from "./SAAuditLog";
import SAPortalHealth from "./SAPortalHealth";
import SASecurity from "./SASecurity";

// Styles
import "../../assets/styles/SuperAdmin/SATheme.css";

const SA_COLORS = {
  bg: "#120808",      // Deep Red-Black
  surface: "#1D0C0C", // Dark Maroon
  card: "#261212",    // Rich Crimson Background
  cardHover: "#2F1818",
  border: "#3F1B1B",
  text: "#FEEBEB",
  muted: "#B28585",
  dim: "#5D3A3A",
  accent: "#E53935",  // Primary Company Red
  red: "#FF5252",
  orange: "#FF8A65",
};

const SA_ROLES_BASE = [
  { id: "superadmin", name: "Super Admin", color: "#E53935", icon: "🛡️", desc: "Full unrestricted access to all modules, settings, and role management", users: [], locked: true },
  { id: "hr", name: "HR Admin", color: "#3b82f6", icon: "👔", desc: "Manage employees, interns, leave, payroll, performance, and announcements", users: [] },
  { id: "itadmin", name: "IT Admin", color: "#FDAB3D", icon: "🖥️", desc: "Manage assets, credentials, IT requests, employee IT profiles, and action logs", users: [] },
  { id: "pm", name: "Project Manager", color: "#B71C1C", icon: "📊", desc: "Manage assigned projects, team progress, leave approvals, documents, and announcements", users: [] },
  { id: "employee", name: "Employee", color: "#2563eb", icon: "👤", desc: "View personal dashboard, attendance, leave balance, payroll slips, and assets", users: [] },
  { id: "intern", name: "Intern", color: "#A29BFE", icon: "🎓", desc: "View intern dashboard, onboarding progress, attendance, leave summary, and chat", users: [] },
];

const SA_MODULES = [
  { id: "dashboard", name: "Dashboard", cat: "Core" }, { id: "profile", name: "Profile", cat: "Core" }, { id: "onboarding", name: "Onboarding", cat: "Core" },
  { id: "attendance", name: "Attendance / Leave", cat: "Operations" }, { id: "payroll", name: "Payroll", cat: "Operations" }, { id: "salary_adv", name: "Salary Advances", cat: "Operations" }, { id: "performance", name: "Performance", cat: "Operations" },
  { id: "emp_mgmt", name: "Employee Mgmt", cat: "People" }, { id: "intern_mgmt", name: "Intern Mgmt", cat: "People" },
  { id: "asset_mgmt", name: "Asset Management", cat: "IT" }, { id: "credentials", name: "Credentials & Access", cat: "IT" }, { id: "it_profiles", name: "IT Profiles", cat: "IT" }, { id: "it_requests", name: "IT Requests", cat: "IT" }, { id: "exit_checklist", name: "Exit Checklist", cat: "IT" },
  { id: "proj_mgmt", name: "Project Management", cat: "PM" }, { id: "proj_docs", name: "Project Documents", cat: "PM" }, { id: "proj_chat", name: "Team Chat", cat: "PM" }, { id: "proj_leave", name: "Leave / WFH Approval", cat: "PM" },
  { id: "action_logs", name: "Action Logs", cat: "Admin" }, { id: "announcements", name: "Announcements", cat: "Admin" }, { id: "calendar", name: "Calendar", cat: "Admin" }, { id: "notes", name: "Notes", cat: "Admin" }, { id: "chat", name: "Chat", cat: "Admin" },
  { id: "roles_priv", name: "Roles & Privileges", cat: "Super Admin" },
];

function SuperAdminPanel({ onLogout }) {
  const [page, setPage] = useState("overview");
  const [perms, setPerms] = useState({});
  const [toast, setToast] = useState(null);
  const [time, setTime] = useState(new Date());
  const [selectedRole, setSelectedRole] = useState(null);
  const [saUsers, setSaUsers] = useState([]);
  const [saAuditLogs, setSaAuditLogs] = useState([]);
  const [saCreds, setSaCreds] = useState([]);
  const [securitySettings, setSecuritySettings] = useState({});
  const [authenticated, setAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState("");

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", status: "", email: "" });
  const [showCredAdd, setShowCredAdd] = useState(false);
  const [credForm, setCredForm] = useState({ id: "", password: "", role: "hr", name: "", email: "" });
  const [editCred, setEditCred] = useState(null);
  const [editCredForm, setEditCredForm] = useState({ password: "", name: "" });

  const PIN = "0000";

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchAdminData();
    }
  }, [authenticated]);

  const fetchAdminData = async () => {
    try {
      const data = await api.getAdminOverview();
      // data: { roles, modules, permissions, users, auditLog, securitySettings }
      setSaUsers(data.users || []);
      setPerms(data.permissions || {});
      setSaAuditLogs(data.auditLog || []);
      const settingsMap = {};
      (data.securitySettings || []).forEach(s => settingsMap[s.setting_key] = s.setting_value === 'true');
      setSecuritySettings(settingsMap);

      // Merge roles with base config for icons/colors if not in DB
      const mergedRoles = SA_ROLES_BASE.map(br => {
        const dbRole = (data.roles || []).find(dr => dr.role_id === br.id);
        return {
          ...br,
          ...dbRole,
          users: (data.users || []).filter(u => u.role === br.id).map(u => ({ name: u.name, avatar: u.name.substring(0, 2).toUpperCase() }))
        };
      });
      // Handle custom roles from DB too
      (data.roles || []).forEach(dr => {
        if (!mergedRoles.find(r => r.id === dr.role_id)) {
          mergedRoles.push({
            id: dr.role_id,
            name: dr.name,
            color: dr.color || SA_COLORS.accent,
            icon: dr.icon || "🛡️",
            desc: dr.description || "Custom Role",
            users: (data.users || []).filter(u => u.role === dr.role_id).map(u => ({ name: u.name, avatar: u.name.substring(0, 2).toUpperCase() })),
            locked: dr.locked
          });
        }
      });
      // For now, if DB is empty, use BAES
      // SA_ROLES = mergedRoles;
    } catch (err) {
      showToast("Error fetching admin data: " + err.message, "error");
    }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handlePermChange = (roleId, modId, level) => {
    if (roleId === "superadmin") return;
    setPerms(p => ({ ...p, [roleId]: { ...p[roleId], [modId]: level } }));
  };

  const handleBulkSavePermissions = async () => {
    try {
      const arr = [];
      SA_ROLES.filter(r => r.id !== "superadmin").forEach(r => {
        SA_MODULES.forEach(m => {
          const level = perms[r.id]?.[m.id] ?? "none";
          arr.push({ roleId: r.id, moduleId: m.id, level });
        });
      });
      if (arr.length === 0) return showToast("No permissions to save");
      await api.bulkUpdatePermissions(arr);
      // Refresh data to reflect changes in role definition
      await fetchAdminData();
      showToast(`Permissions saved successfully (${arr.length} cells)`);
    } catch (err) {
      showToast(err.message || "Failed to save", "error");
    }
  };

  const onUpdateUser = async (userId, action) => {
    const u = saUsers.find(x => x.id === userId);
    if (!u) return;
    if (action === 'role') {
      setEditForm({ role: u.role, status: u.status === 'INACTIVE' || u.status === 'inactive' ? 'inactive' : 'active', email: u.email });
      setEditUser(u);
    } else {
      // block/unblock - only for non-admin users
      const adminRoles = ['hr', 'pm', 'itadmin', 'superadmin'];
      if (adminRoles.includes(u.role)) {
        showToast('Cannot block admin users', "error");
        return;
      }
      const newStatus = action === 'block' ? 'INACTIVE' : 'ACTIVE';
      try {
        await api.updateUserStatus(u.emp_id, newStatus);
        await fetchAdminData();
        showToast(`${u.name} ${action === 'block' ? 'blocked' : 'unblocked'} successfully`);
      } catch (e) {
        showToast(e.message || `Failed to ${action} user`, "error");
      }
    }
  };

  const saveEditUser = async () => {
    if (!editUser) return;
    try {
      // Update role first
      await api.changeUserRole(editUser.emp_id, editForm.role);
      // Update status based on role - admin roles get ACTIVE, others get appropriate status
      const adminRoles = ['hr', 'pm', 'itadmin', 'superadmin'];
      const newStatus = adminRoles.includes(editForm.role) ? 'ACTIVE' : (editForm.status === 'inactive' ? 'INACTIVE' : 'ACTIVE');
      await api.updateUserStatus(editUser.emp_id, newStatus);
      // Refresh data to reflect changes everywhere (role definition, user list, control center counts)
      await fetchAdminData();
      showToast(`User ${editUser.name} updated successfully. Role changed to ${SA_ROLES.find(r => r.id === editForm.role)?.name || editForm.role}`);
      setEditUser(null);
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const addCred = async () => {
    if (!credForm.id.trim() || !credForm.password.trim() || !credForm.name.trim()) return;
    try {
      await api.createUser({
        empId: credForm.id.trim(),
        name: credForm.name.trim(),
        email: credForm.email || credForm.id.trim().toLowerCase().replace(/[^a-z0-9]/g, '') + '@amshrms.in',
        role: credForm.role,
        password: credForm.password.trim()
      });
      showToast('User created successfully');
      setShowCredAdd(false);
      fetchAdminData();
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const openEditCred = (c) => {
    setEditCredForm({ password: "", name: c.name });
    setEditCred(c);
  };

  const saveEditCred = async () => {
    if (!editCred) return;
    try {
      await api.resetUserPassword(editCred.emp_id, editCredForm.password);
      showToast('Password reset successfully');
      setEditCred(null);
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const deleteCred = (c) => {
    // Implement delete user API if needed
    showToast('User deletion not implemented in this demo');
  };

  const onSecurityToggle = async (key) => {
    const newVal = !securitySettings[key];
    try {
      await api.updateSecurity(key, String(newVal));
      setSecuritySettings(p => ({ ...p, [key]: newVal }));
      showToast("Security policy updated");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const hour = time.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Access Gate
  if (!authenticated) {
    return (
      <div className="sa-gate">
        <div className="sa-gate-box">
          <div className="sa-gate-icon">🛡️</div>
          <h1 className="sa-gate-title">Super Admin Access</h1>
          <p className="sa-gate-desc">This control panel is restricted. Enter your security PIN to continue.</p>
          <div className="sa-gate-pin-display">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`sa-gate-pin-digit ${pinInput[i] ? 'filled' : ''}`}>
                {pinInput[i] ? "•" : ""}
              </div>
            ))}
          </div>
          <div className="sa-gate-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "⌫"].map((n, i) => (
              n !== null ? (
                <button key={i} className="sa-gate-key" onClick={() => {
                  if (n === "⌫") setPinInput(p => p.slice(0, -1));
                  else if (pinInput.length < 4) setPinInput(p => p + String(n));
                }}>{n}</button>
              ) : <div key={i} />
            ))}
          </div>
          <button className={`sa-gate-auth-btn ${pinInput.length === 4 ? 'active' : ''}`} onClick={() => {
            if (pinInput === PIN) setAuthenticated(true);
            else { setPinInput(""); showToast("Invalid PIN", "error"); }
          }}>Authenticate</button>
         {/* <p className="sa-gate-hint">Demo PIN: 0000</p> */}
         <p></p>
          <button onClick={onLogout} className="sa-gate-back-btn">← Back to Login</button>
        </div>
        {toast && (<div className={`sa-toast ${toast.type}`}>{toast.msg}</div>)}
        <style>{`
          .sa-gate { height: 100vh; background: #eef2f8; display: flex; align-items: center; justify-content: center; color: #1f3756; font-family: 'DM Sans', sans-serif; }
          .sa-gate-box { text-align: center; max-width: 400px; }
          .sa-gate-icon { width: 80px; height: 80px; border-radius: 20px; background: linear-gradient(135deg, rgba(255, 136, 112, 0.14), rgba(255, 193, 168, 0.14)); border: 2px solid rgba(255, 136, 112, 0.35); display: flex; align-items: center; justify-content: center; font-size: 36px; margin: 0 auto 24px; }
          .sa-gate-title { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 800; margin: 0 0 8px; }
          .sa-gate-desc { color: #6f85a4; font-size: 14px; margin-bottom: 32px; line-height: 1.6; }
          .sa-gate-pin-display { display: flex; gap: 12px; justify-content: center; margin-bottom: 24px; }
          .sa-gate-pin-digit { width: 52px; height: 58px; border-radius: 12px; font-size: 24px; font-weight: 800; background: #ffffff; border: 2px solid #2f6fe4; display: flex; align-items: center; justify-content: center; color: #2f6fe4; transition: all 0.2s; }
          .sa-gate-pin-digit.filled { background: #ffffff; border-color: #2f6fe4; color: #2f6fe4; box-shadow: 0 0 0 3px rgba(47,111,228,0.12); }
          .sa-gate-keypad { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; max-width: 220px; margin: 0 auto 24px; }
          .sa-gate-key { width: 56px; height: 52px; border-radius: 12px; border: 1px solid #d9e2ef; background: #ffffff; color: #1f3756; font-size: 20px; font-weight: 700; cursor: pointer; transition: all 0.15s; }
          .sa-gate-key:hover { background: #f8fbff; border-color: #2f6fe4; }
          .sa-gate-auth-btn { width: 100%; max-width: 220px; padding: 14px 0; border-radius: 12px; border: none; background: #cfd8e8; color: #90a2bc; font-size: 14px; font-weight: 700; cursor: not-allowed; transition: all 0.2s; }
          .sa-gate-auth-btn.active { background: linear-gradient(135deg, #ff5a4f, #e67b00); color: #fff; cursor: pointer; box-shadow: 0 6px 24px rgba(255, 120, 54, 0.24); }
          .sa-gate-hint { color: #90a2bc; font-size: 11px; margin-top: 16px; }
          .sa-gate-back-btn { margin-top: 16px; background: #ffffff; border: 1px solid #d9e2ef; color: #6f85a4; padding: 8px 20px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; }
          .sa-toast { position: fixed; top: 20px; right: 20px; z-index: 999; padding: 12px 24px; border-radius: 12px; font-weight: 700; font-size: 13px; color: #fff; }
          .sa-toast.success { background: #2563eb; box-shadow: 0 8px 32px rgba(0, 214, 143, 0.3); }
          .sa-toast.error { background: #E53935; box-shadow: 0 8px 32px rgba(229, 57, 53, 0.3); }
        `}</style>
      </div>
    );
  }

  const modCount = SA_MODULES.length;
  const SA_ROLES = SA_ROLES_BASE.map(br => ({
    ...br,
    users: saUsers.filter(u => u.role === br.id).map(u => ({ name: u.name, avatar: u.name.substring(0, 2).toUpperCase() }))
  }));

  return (
    <div className="sa-container">
      <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {toast && (
        <div className={`sa-toast ${toast.type}`} style={{ 
          position: "fixed", 
          top: 20, 
          right: 20, 
          zIndex: 9999,
          padding: "14px 24px",
          borderRadius: 12,
          fontWeight: 700,
          fontSize: 13,
          color: "#fff",
          boxShadow: toast.type === "success" ? "0 8px 32px rgba(0, 214, 143, 0.4)" : "0 8px 32px rgba(229, 57, 53, 0.4)",
          background: toast.type === "success" ? "#2563eb" : "#E53935",
          minWidth: "280px",
          maxWidth: "400px",
          wordWrap: "break-word"
        }}>{toast.msg}</div>
      )}

      <SASidebar page={page} setPage={setPage} />

      <main className="sa-main">
        <SAHeader
          page={page}
          time={time}
          setAuthenticated={setAuthenticated}
          onLogout={onLogout}
          activeLabel={(SA_NAV_ITEMS.find(n => n.id === page)?.label) || "Overview"}
          onNavigate={setPage}
        />

        <div className="sa-content">
          {page === "overview" && (
            <SAOverview
              perms={perms}
              modCount={modCount}
              SA_ROLES={SA_ROLES}
              SA_AUDIT_LOG={saAuditLogs}
              SA_ALL_USERS={saUsers}
              setPage={setPage}
              greeting={greeting}
            />
          )}

          {page === "notifications" && <SANotifications />}

          {page === "roles" && (
            <SARoles
              SA_ROLES={SA_ROLES}
              perms={perms}
              modCount={modCount}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
            />
          )}

          {page === "matrix" && (
            <SAPermMatrix
              perms={perms}
              SA_MODULES={SA_MODULES}
              SA_ROLES={SA_ROLES}
              handlePermChange={handlePermChange}
              onSave={handleBulkSavePermissions}
            />
          )}

          {page === "users" && (
            <SAUserMgmt
              users={saUsers}
              SA_ROLES={SA_ROLES}
              onUpdateUser={onUpdateUser}
              onResetPass={(id) => {
                const u = saUsers.find(x => x.id === id);
                if (u) { setEditCred(u); setEditCredForm({ name: u.name, password: "" }); setPage("credentials"); }
              }}
              onRefresh={fetchAdminData}
            />
          )}

          {page === "credentials" && (
            <SACredentials
              saCreds={saUsers.filter(u => ['hr', 'pm', 'itadmin', 'hr_admin', 'project_mgr', 'it_admin'].includes(u.role)).map(u => ({ id: u.emp_id, emp_id: u.emp_id, name: u.name, role: ['hr', 'hr_admin'].includes(u.role) ? 'hr' : ['pm', 'project_mgr'].includes(u.role) ? 'pm' : 'itadmin', password: '****', pwVisible: false }))}
              onRefresh={fetchAdminData}
            />
          )}

          {page === "audit" && <SAAuditLog logs={saAuditLogs} />}

          {page === "portals" && <SAPortalHealth SA_ALL_USERS={saUsers} />}

          {page === "security" && <SASecurity settings={securitySettings} onToggle={onSecurityToggle} />}

          {/* User Edit Modal (Global for now) */}
          {editUser && (
            <div onClick={() => setEditUser(null)} style={{ position: "fixed", inset: 0, background: "rgba(6,8,12,0.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div onClick={e => e.stopPropagation()} className="sa-card" style={{ width: 460, maxWidth: "92vw" }}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20 }}>Edit User: {editUser.name}</div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--sa-muted)", marginBottom: 4 }}>ROLE</div>
                  <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="sa-cred-input">
                    {SA_ROLES_BASE.filter(r => r.id !== "super_admin").map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--sa-muted)", marginBottom: 4 }}>STATUS</div>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="sa-cred-input">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <div style={{ fontSize: 10, color: "var(--sa-muted)", marginTop: 4 }}>
                    Note: Status will be set based on role. Admin roles get ACTIVE status.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
                  <button onClick={() => setEditUser(null)} className="sa-action-btn">Cancel</button>
                  <button onClick={saveEditUser} style={{ background: "var(--sa-accent)", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Save Changes</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default SuperAdminPanel;
